import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import type { DealList } from "./dealsCrmActions";
import type { DealFilters } from "./useDealFilters";
import type { DrawState } from "./usePolygonFilter";

const ALL = "__all"; // Radix Select can't use "" as an item value

interface FilterBarProps {
  filters: DealFilters;
  setFilters: (patch: Partial<DealFilters>) => void;
  options: { dates: string[]; counties: string[]; tiers: string[] };
  lists: DealList[];
  drawState: DrawState;
  onDrawToggle: () => void;
  countsLabel: string;
}

const FilterSelect = ({
  value,
  onChange,
  placeholder,
  items,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  items: { value: string; label: string }[];
  className?: string;
}) => (
  <Select
    value={value || ALL}
    onValueChange={(v) => onChange(v === ALL ? "" : v)}
  >
    <SelectTrigger size="sm" className={cn("w-auto min-w-32", className)}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {items.map((item) => (
        <SelectItem key={item.value} value={item.value}>
          {item.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const drawLabel: Record<DrawState, string> = {
  idle: "✏️ Draw area",
  drawing: "✓ Finish",
  active: "✕ Clear area",
};

export const FilterBar = ({
  filters,
  setFilters,
  options,
  lists,
  drawState,
  onDrawToggle,
  countsLabel,
}: FilterBarProps) => (
  <div className="flex flex-wrap items-center gap-2 border-b bg-background px-3 py-2">
    <Input
      value={filters.zip}
      onChange={(e) => setFilters({ zip: e.target.value })}
      placeholder="ZIP"
      className="h-8 w-24"
      inputMode="numeric"
    />
    <FilterSelect
      value={filters.date}
      onChange={(date) => setFilters({ date })}
      placeholder="All sale dates"
      items={options.dates.map((d) => ({ value: d, label: d }))}
    />
    <FilterSelect
      value={filters.county}
      onChange={(county) => setFilters({ county })}
      placeholder="All counties"
      items={options.counties.map((c) => ({ value: c, label: c }))}
    />
    <FilterSelect
      value={filters.tier}
      onChange={(tier) => setFilters({ tier })}
      placeholder="All tiers"
      items={options.tiers.map((t) => ({ value: t, label: t }))}
    />
    <FilterSelect
      value={filters.list}
      onChange={(list) => setFilters({ list })}
      placeholder="All lists"
      items={lists.map((l) => ({ value: String(l.id), label: l.name }))}
    />
    <label className="flex items-center gap-1.5 text-sm">
      <Switch
        checked={filters.showAuctions}
        onCheckedChange={(showAuctions) => setFilters({ showAuctions })}
      />
      🏛️ Auctions
    </label>
    <label className="flex items-center gap-1.5 text-sm">
      <Switch
        checked={filters.showProbates}
        onCheckedChange={(showProbates) => setFilters({ showProbates })}
      />
      ⚖️ Probates
    </label>
    <Button
      variant={drawState === "idle" ? "outline" : "default"}
      size="sm"
      onClick={onDrawToggle}
    >
      {drawLabel[drawState]}
    </Button>
    <span className="ml-auto text-sm text-muted-foreground">{countsLabel}</span>
  </div>
);
